import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { finalize, forkJoin, map, Observable } from 'rxjs';
import {
  getDisplayedNotifications, hasKnownMessageType, prioritizeNotifications, PRODUCT_NOTIFICATION_ACTIONS
} from 'core/tooltip-notification/product-notification-priority';
import { ProductNotification } from 'src/app/modules/pos/quotes/entities/products.class';
import { ProductTemplateValues } from '../../entities/product-entity';
import {
  PRODUCT_NOTIFICATION_DIALOG_MODES, ProductNotificationDialogComponent, ProductNotificationDialogData, ProductNotificationDialogResult
} from '../../product-notification-dialog/product-notification-dialog.component';
import { ProductsService } from '../products-service/products.service';

export interface ProductToValidate {
  catalogCode: string;
  productNumber: string;
}

export const PRODUCT_DETAILS_ROUTE = '/tools/product';
export const DEFAULT_CATALOG_CODE = 'IUS';

@Injectable({
  providedIn: 'root'
})
export class ProductValidationService {
  isValidating = false;

  constructor(
    private productsService: ProductsService,
    private dialog: MatDialog,
    private router: Router
  ) { }

  /**
   * Validates the product notifications and opens its PDP, showing the notification modal first when needed.
   * @param product - The product to open
   */
  validateAndOpen(product: ProductToValidate): void {
    if (this.isValidating) {
      return;
    }
    this.validateStep(product.catalogCode ?? DEFAULT_CATALOG_CODE, product.productNumber, new Set<string>());
  }

  /**
   * Gets the limited marketing notifications to display, with the same priority rules as the quote tooltip
   * @param productNumber - The current product number
   * @param customerId - The selected customer or lead id
   * @param isLead - Whether customerId is a lead id
   */
  getLimitedMarketingNotifications(productNumber: string, customerId: string, isLead: boolean): Observable<ProductNotification[]> {
    return forkJoin([
      this.productsService.getTemplateValues(),
      this.productsService.getLimitedMarketingNotifications(productNumber, customerId, isLead)
    ]).pipe(map(([templateValues, notifications]) => this.getDisplayed(notifications, templateValues)));
  }

  /**
   * Shows the limited marketing notifications for the customer selected on the PDP. The OK button only closes the modal,
   * Go to supersede validates and opens the superseding product.
   * @param productNumber - The current product number
   * @param notifications - The limited marketing notifications
   * @param catalogCode - The catalog code of the current PDP, used for the superseding product
   */
  openLimitedMarketingDialog(productNumber: string, notifications: ProductNotification[], catalogCode: string): void {
    this.openDialog({
      productNumber,
      notifications,
      mode: PRODUCT_NOTIFICATION_DIALOG_MODES.limitedMarketing
    }).afterClosed().subscribe((result?: ProductNotificationDialogResult) => {
      if (result?.action === PRODUCT_NOTIFICATION_ACTIONS.openSupersede) {
        this.validateStep(catalogCode || DEFAULT_CATALOG_CODE, result.supersedeProductNumber, new Set<string>([productNumber]));
      }
    });
  }

  private validateStep(catalogCode: string, productNumber: string, visited: Set<string>) {
    visited.add(productNumber);
    this.isValidating = true;
    forkJoin([
      this.productsService.getTemplateValues(),
      this.productsService.getProductNotifications(productNumber)
    ]).pipe(
      finalize(() => this.isValidating = false)
    ).subscribe({
      next: ([templateValues, notifications]) =>
        this.handleNotifications(catalogCode, productNumber, notifications, templateValues, visited),
      // ApiService already shows the error toast
      error: () => undefined
    });
  }

  private handleNotifications(
    catalogCode: string,
    productNumber: string,
    notifications: ProductNotification[],
    templateValues: ProductTemplateValues,
    visited: Set<string>
  ) {
    const displayed = this.getDisplayed(notifications, templateValues);
    if (!displayed.length) {
      this.navigate(catalogCode, productNumber);
      return;
    }

    this.openDialog({
      productNumber,
      notifications: displayed,
      mode: PRODUCT_NOTIFICATION_DIALOG_MODES.entryValidation,
      messageCodes: templateValues.NotificationMessageCode,
      hideSupersede: visited.has(displayed[0].supersedeProductNumber)
    }).afterClosed().subscribe((result?: ProductNotificationDialogResult) => {
      switch (result?.action) {
        case PRODUCT_NOTIFICATION_ACTIONS.ok:
        case PRODUCT_NOTIFICATION_ACTIONS.openCurrent:
          this.navigate(catalogCode, productNumber);
          break;
        case PRODUCT_NOTIFICATION_ACTIONS.openSupersede:
          this.validateStep(catalogCode, result.supersedeProductNumber, visited);
          break;
        default:
          // Cancel, backdrop click or Escape
          break;
      }
    });
  }

  /**
   * Applies the quote tooltip priority and visibility rules. Notifications with an unknown message type are skipped
   * instead of throwing, since the tooltip has no group to display them in.
   */
  private getDisplayed(notifications: ProductNotification[], templateValues: ProductTemplateValues): ProductNotification[] {
    const messageTypes = templateValues.NotificationMessageType;
    const known = (notifications ?? []).filter(notification => {
      const isKnown = hasKnownMessageType(notification, messageTypes);
      if (!isKnown) {
        console.warn('Product notification skipped, unknown message type', notification);
      }
      return isKnown;
    });
    return getDisplayedNotifications(prioritizeNotifications(known, messageTypes));
  }

  private openDialog(data: ProductNotificationDialogData) {
    return this.dialog.open<ProductNotificationDialogComponent, ProductNotificationDialogData, ProductNotificationDialogResult>(
      ProductNotificationDialogComponent, { data, restoreFocus: this.shouldRestoreFocus(data) });
  }

  private shouldRestoreFocus(data: ProductNotificationDialogData): boolean {
    return data.mode === PRODUCT_NOTIFICATION_DIALOG_MODES.limitedMarketing && !data.notifications[0]?.supersedeProductNumber;
  }

  private navigate(catalogCode: string, productNumber: string) {
    this.router.navigate([PRODUCT_DETAILS_ROUTE, catalogCode, productNumber]);
  }
}
